"use client";

import * as amplitude from "@amplitude/analytics-browser";

/**
 * Idempotent boot for the Amplitude Browser SDK.
 *
 * Three independent disable gates — all defaulting OFF — keep dev laptops
 * silent unless explicitly opted in. They mirror the Sentry contract
 * documented in `.cursor/rules/80-sentry.mdc`:
 *
 *   1. `NEXT_PUBLIC_AMPLITUDE_ENABLED !== "true"` → `init()` returns early.
 *   2. `NEXT_PUBLIC_AMPLITUDE_API_KEY` empty → `init()` returns early.
 *   3. Calling `track()` before `init()` queues the event; if init is
 *      then skipped the queue is dropped, so nothing ever leaves a
 *      disabled environment and call sites never branch on "is
 *      amplitude up?".
 *
 * Every consumer should funnel through `track()` in `./track.ts` — never
 * import `@amplitude/analytics-browser` directly from product code. We
 * enforce that with `eslint-plugin` `no-restricted-imports` is overkill
 * for this codebase size; convention + this comment are the contract.
 */

/**
 * Lifecycle of the SDK as seen by `track()`:
 *
 *   pending            → nothing decided yet; events are queued.
 *   awaiting_identity  → SDK is up, but the Auth0 identity (user_id +
 *                        email_domain) is not applied yet; events stay
 *                        queued so they never land without a company.
 *   ready              → events go straight to the SDK.
 *   disabled           → init was skipped; everything is a no-op.
 *
 * The queue exists because child effects run before the provider's init
 * effect: a page-mount event on a hard load (or a new tab) would otherwise
 * fire before init and be dropped.
 */
export type AmplitudeState = "pending" | "awaiting_identity" | "ready" | "disabled";

const MAX_PENDING_EVENTS = 100;

let state: AmplitudeState = "pending";
let pendingEvents: Array<{ name: string; props: Record<string, unknown> }> = [];

export function getAmplitudeState(): AmplitudeState {
    return state;
}

/**
 * Whether the SDK is initialized. Identity wiring consults this; `track()`
 * uses the finer-grained `getAmplitudeState()`.
 */
export function isAmplitudeEnabled(): boolean {
    return state === "awaiting_identity" || state === "ready";
}

/** Holds an event until identity is settled. Beyond the cap, events are dropped. */
export function enqueuePendingEvent(name: string, props: Record<string, unknown>): void {
    if (pendingEvents.length >= MAX_PENDING_EVENTS) return;
    pendingEvents.push({ name, props });
}

/**
 * Called by the provider once the Auth0 identity is applied (or known to be
 * absent). Flushes queued events in order so they carry user_id and
 * email_domain.
 */
export function markIdentitySettled(): void {
    if (state !== "awaiting_identity") return;
    state = "ready";
    const queued = pendingEvents;
    pendingEvents = [];
    for (const event of queued) amplitude.track(event.name, event.props);
}

export type InitInput = {
    apiKey: string | undefined;
    enabled: boolean;
    environment: string;
    appVersion?: string;
    buildVariant?: string;
};

/**
 * Initializes the SDK exactly once. Subsequent calls are no-ops, so the
 * provider can call this from a `useEffect` without worrying about
 * double-invoke under React StrictMode.
 *
 * Returns `true` when the SDK is now active, `false` when init was
 * skipped — useful for the provider to short-circuit identity / page-view
 * wiring when there's nothing listening anyway.
 */
export function initAmplitude(input: InitInput): boolean {
    if (isAmplitudeEnabled()) return true;
    if (!input.enabled || !input.apiKey) {
        state = "disabled";
        pendingEvents = [];
        return false;
    }

    amplitude.init(input.apiKey, {
        // EU residency. Estaid's Amplitude org is provisioned in Frankfurt;
        // hard-coded because (a) it's a public, well-known constant per
        // region — not a secret — and (b) `serverZone` is type-safe in v2
        // ("US" | "EU"), eliminating the typo class of bugs you'd get from
        // sourcing the URL from an env var.
        serverZone: "EU",
        // We fire `page_view` ourselves from `usePageViewTracking()` so it
        // tracks every App Router transition, not just the subset Amplitude's
        // popstate-based autocapture catches. Sessions stay autocaptured
        // because the SDK's session handling is a non-trivial chunk we don't
        // want to reimplement.
        defaultTracking: {
            sessions: true,
            pageViews: false,
            formInteractions: false,
            fileDownloads: false,
            attribution: false,
        },
        // Default opt-out flag is false — when a consent banner ships,
        // route its "rejected" branch through `amplitude.setOptOut(true)`.
        optOut: false,
    });

    // Environment and release travel on every event (SDK session events
    // included) so dashboards can split on them. They used to be `setOnce`
    // user properties, which froze a user's first-ever environment forever.
    const environmentProps: Record<string, string> = { environment: input.environment };
    if (input.appVersion) environmentProps.app_version = input.appVersion;
    if (input.buildVariant) environmentProps.build_variant = input.buildVariant;
    amplitude.add({
        name: "estaid-environment",
        type: "enrichment",
        execute: async (event) => ({ ...event, event_properties: { ...event.event_properties, ...environmentProps } }),
    });

    state = "awaiting_identity";
    return true;
}

/**
 * Test-only hook for resetting init state between test cases. Not exported
 * from the public surface (no barrel re-export); imported directly by
 * specs that need to re-initialize the SDK.
 */
export function __resetAmplitudeForTests(): void {
    state = "pending";
    pendingEvents = [];
}
